// Shared delivery-time filtering/sorting helper for MULTI-STORE discovery
// surfaces (home restaurant list, category restaurant list, Explore/store
// search). This is intentionally separate from `storeFilters.js`, which
// covers filtering *within* a single store's item list - delivery time is a
// store-level value, so it never belonged there (every item in one store
// shares the same ETA).
//
// `restaurants.delivery_time` is a free-form admin string today (e.g. "25",
// "30-40", "2-3 Days", or "0" meaning "unset"). This helper normalizes the
// minute-based (Sprint/quick-commerce) values it can safely parse and
// treats anything else - in particular day-based Relay-style values - as
// "unknown", rather than guessing.

// Matches "2-3 Days", "1 Day", "Next Day" etc. so day-based ETAs are never
// misread as minutes.
const DAY_BASED_PATTERN = /day/i;

/**
 * Parses a restaurant's `delivery_time` into a whole number of minutes, or
 * `null` if it can't be safely interpreted as minutes (missing, "0", a
 * day-based Relay-style value, or unparseable free text).
 *
 * For a range like "30-40", the upper bound is used - the promised/"worst
 * case" ETA - so "fastest delivery" sorting/filtering never overstates how
 * quick a store actually is.
 */
export function parseDeliveryMinutes(deliveryTimeRaw) {
  if (deliveryTimeRaw === null || deliveryTimeRaw === undefined) return null;

  const value = String(deliveryTimeRaw).trim();
  if (!value || value === "0") return null;
  if (DAY_BASED_PATTERN.test(value)) return null;

  const numbers = value.match(/\d+(\.\d+)?/g);
  if (!numbers || !numbers.length) return null;

  const parsed = numbers.map((n) => parseFloat(n)).filter((n) => Number.isFinite(n) && n > 0);
  if (!parsed.length) return null;

  return Math.round(Math.max(...parsed));
}

export const DELIVERY_TIME_OPTIONS = [
  { value: "15", label: "Under 15 mins" },
  { value: "30", label: "Under 30 mins" },
  { value: "45", label: "Under 45 mins" },
  { value: "60", label: "Under 60 mins" },
];

/**
 * Sorts restaurants by their parsed delivery minutes, fastest first.
 * Restaurants whose delivery_time can't be parsed as minutes (e.g. missing,
 * or a Relay-style day-based value) are pushed to the end rather than
 * dropped, so the list never silently loses stores.
 */
export function sortRestaurantsByDeliveryTime(restaurants) {
  if (!Array.isArray(restaurants)) return [];

  return [...restaurants].sort((a, b) => {
    const aMins = parseDeliveryMinutes(a.delivery_time);
    const bMins = parseDeliveryMinutes(b.delivery_time);
    if (aMins === null && bMins === null) return 0;
    if (aMins === null) return 1;
    if (bMins === null) return -1;
    return aMins - bMins;
  });
}

/**
 * Filters restaurants to those whose delivery_time falls within at least
 * one of the selected "Under N mins" tiers. Restaurants with an unparseable
 * delivery_time are excluded once a filter is active (there's nothing to
 * confidently match against), matching how price/rating tiers behave
 * elsewhere in the app.
 */
export function filterRestaurantsByDeliveryTime(restaurants, selectedTiers) {
  if (!Array.isArray(restaurants)) return [];
  if (!selectedTiers || !selectedTiers.length) return restaurants;

  return restaurants.filter((restaurant) => {
    const mins = parseDeliveryMinutes(restaurant.delivery_time);
    if (mins === null) return false;

    return selectedTiers.some((tier) => {
      const max = parseInt(tier, 10);
      return Number.isFinite(max) && mins <= max;
    });
  });
}
